/**
 * Teams — a drum group, a canoe crew, a horseshoe pair (D33).
 *
 * A team is one entrant: it is scored and placed as a unit, while its members stay people so
 * payouts can still land on a person. The distribution note records how a shared prize was
 * actually split, because that is the treasurer's question, not the scorekeeper's.
 */

import { z } from "zod";
import {
  emailSchema,
  longTextSchema,
  optionalText,
  phoneSchema,
  reasonSchema,
  shortTextSchema,
  ulidSchema,
} from "./primitives.js";
import {
  consentEntrySchema,
  deviceIdSchema,
  optionalAge,
  participantSchema,
  stationSchema,
} from "./registration.js";

export const createTeamSchema = z.object({
  competitionId: ulidSchema,
  categoryId: ulidSchema.optional(),
  name: shortTextSchema,
  contactEmail: optionalText(emailSchema),
  contactPhone: optionalText(phoneSchema),
  communityAffiliation: optionalText(shortTextSchema),
  captainParticipantId: ulidSchema.optional(),
  notes: optionalText(longTextSchema),
});

export const updateTeamSchema = z.object({
  name: shortTextSchema.optional(),
  categoryId: ulidSchema.nullable().optional(),
  contactEmail: optionalText(emailSchema),
  contactPhone: optionalText(phoneSchema),
  communityAffiliation: optionalText(shortTextSchema),
  captainParticipantId: ulidSchema.nullable().optional(),
  /** How a shared prize was split — free text, because communities decide this. */
  distributionNote: optionalText(longTextSchema),
  notes: optionalText(longTextSchema),
});

export const addTeamMemberSchema = z.object({
  participantId: ulidSchema,
  /** Free text — "captain", "second drum", whatever the community calls it. */
  roleLabel: optionalText(shortTextSchema),
});

export const setTeamCaptainSchema = z.object({
  participantId: ulidSchema,
});

export const withdrawTeamSchema = z.object({
  reason: optionalText(reasonSchema),
});

/**
 * One member in a register-together submission (D57).
 *
 * A member is either an existing participant (`participantId`) or a new person named inline. The
 * two are distinguished by whether `participantId` is present, so the same shape serves the desk
 * (where staff often pick a returning participant) and the public page (where every member is new).
 */
export const teamMemberEntrySchema = participantSchema
  // Identity is all optional here: an existing member carries only a `participantId`, and a new one
  // is named inline. `notes` is a staff field on a person, not something a team seat carries.
  .omit({ notes: true })
  .partial()
  .extend({
    /** Present when this seat is filled by a participant the system already knows. */
    participantId: ulidSchema.optional(),
    /** How old this member is at the event. Lands on their own registration, not on the person. */
    age: optionalAge,
    /** Free text — "lead singer", "second drum", "stern". */
    roleLabel: optionalText(shortTextSchema),
    /** Marks the seat as the captain. Exactly one member should carry it; the server falls back to
     *  the first member if none is marked. */
    isCaptain: z.boolean().default(false),
    /** The member's own answers to the configured registration form, when the desk collects the
     *  full form per member (D57). Validated a second time on the server against the live version;
     *  an empty object means "names only", the desk's quick path. */
    form: z.record(z.string(), z.unknown()).default({}),
    consents: z.array(consentEntrySchema).default([]),
  })
  .refine((member) => member.participantId != null || (!!member.firstName && !!member.lastName), {
    message: "Each member needs either an existing participant or a first and last name",
    path: ["lastName"],
  });

/**
 * Register a team and its members in one submission (D57) — the "register together" flow, shared by
 * the desk and the public page. The roster bounds (`rosterMin`/`rosterMax`, D55) are enforced by the
 * team registration itself, so this schema only requires that at least one seat is named; the floor
 * and ceiling are the competition's to declare and the register step's to check.
 */
export const registerTeamTogetherSchema = z.object({
  competitionId: ulidSchema,
  categoryId: ulidSchema.optional(),
  name: shortTextSchema,
  contactEmail: optionalText(emailSchema),
  contactPhone: optionalText(phoneSchema),
  communityAffiliation: optionalText(shortTextSchema),
  notes: optionalText(longTextSchema),
  members: z.array(teamMemberEntrySchema).min(1),
  /** Which desk is registering them (D86). A team mints registrations like any other desk act. */
  station: stationSchema.optional(),
  stationDeviceId: deviceIdSchema.optional(),
});

/** The desk's own registration of an already-formed team. Body exists only to carry the station. */
export const registerTeamSchema = z.object({
  station: stationSchema.optional(),
  stationDeviceId: deviceIdSchema.optional(),
});

export type TeamMemberEntryInput = z.infer<typeof teamMemberEntrySchema>;
export type RegisterTeamTogetherInput = z.infer<typeof registerTeamTogetherSchema>;

/**
 * A member in a **public** register-together submission (D57), filling the full configured form.
 *
 * Unlike the desk's, this carries no `participantId`: an anonymous captain typing a name must never
 * be able to attach a stranger's existing record to their team — every public member is a new
 * person, and deduplication stays the registrar's merge job (as it already is for public individual
 * registration).
 *
 * Every member is as fully recorded as an individual self-registrant: the same participant identity
 * columns, the same free-form `form` answers (validated a second time on the server against the live
 * form version), and the same `consents`. "Full per-member" is the whole point — a team of eight is
 * eight people who each answered the community's questions, not one roster of bare names (D57).
 */
export const publicTeamMemberSchema = participantSchema.omit({ notes: true }).extend({
  age: optionalAge,
  roleLabel: optionalText(shortTextSchema),
  /** Marks the seat as the captain. Exactly one member should carry it; the server falls back to
   *  the first member if none is marked. */
  isCaptain: z.boolean().default(false),
  form: z.record(z.string(), z.unknown()).default({}),
  consents: z.array(consentEntrySchema).default([]),
});

/** A team registering itself from the public page (D57). Roster bounds are the register step's. */
export const publicTeamRegistrationSchema = z.object({
  competitionId: ulidSchema,
  categoryId: ulidSchema.optional(),
  sessionId: ulidSchema.optional(),
  name: shortTextSchema,
  contactEmail: optionalText(emailSchema),
  contactPhone: optionalText(phoneSchema),
  communityAffiliation: optionalText(shortTextSchema),
  members: z.array(publicTeamMemberSchema).min(1),
  /** As on an individual public registration: the language each member's confirmation is written
   *  in. */
  locale: z.string().min(2).max(20).optional(),
});

export type PublicTeamMemberInput = z.infer<typeof publicTeamMemberSchema>;
export type PublicTeamRegistrationInput = z.infer<typeof publicTeamRegistrationSchema>;
